/**
 * Alerta sonoro da sala de espera: um "ding-dong" curto quando alguém novo chega pedindo para entrar.
 *
 * O aviso visual (o banner "quer entrar") passa despercebido quando o atendente está em outra aba, na janela flutuante ou
 * olhando o vídeo; o som chama a atenção para decidir a entrada. É gerado na hora (Web Audio, duas notas suaves), sem arquivo
 * de áudio para carregar.
 *
 * Regras:
 * - toca UMA vez por pessoa que chega (cada entrada na sala de espera tem seu id), nunca de novo para quem já estava;
 * - a lista que já existe ao montar não toca (o banner está na tela, e remontar a janela não pode repetir o som);
 * - o navegador pode barrar áudio sem interação da pessoa: sem som e sem erro, o aviso visual continua valendo.
 */
import { useEffect, useRef } from 'react';

/** Duas notas curtas, a segunda mais aguda (A5 e D6): soa como campainha, não como alarme. */
const NOTES = Object.freeze([{ frequency: 880, at: 0 }, { frequency: 1174.66, at: 0.18 }]);
const PEAK_GAIN = 0.18;
const NOTE_SECONDS = 0.45;

function browserAudioContext() {
    const Context = globalThis.AudioContext ?? globalThis.webkitAudioContext;
    return Context ? new Context() : null;
}

/**
 * @param {{ createContext?: () => AudioContext | null }} [options] `createContext`: como obter o contexto de áudio (o do navegador por padrão)
 * @returns {() => boolean} toca o alerta; devolve se conseguiu iniciar o som
 */
export function createWaitingChime({ createContext = browserAudioContext } = {}) {
    let context = null;
    return function play() {
        try {
            context ??= createContext();
            if (!context) return false;
            // O navegador cria o contexto "suspenso" até haver interação: a pessoa já interagiu com a página ao iniciar a chamada
            if (context.state === 'suspended') context.resume?.()?.catch?.(() => {});
            const start = context.currentTime;
            for (const { frequency, at } of NOTES) {
                const oscillator = context.createOscillator();
                const gain = context.createGain();
                oscillator.type = 'sine';
                oscillator.frequency.value = frequency;
                gain.gain.setValueAtTime(0.0001, start + at);
                gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, start + at + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, start + at + NOTE_SECONDS);
                oscillator.connect(gain);
                gain.connect(context.destination);
                oscillator.start(start + at);
                oscillator.stop(start + at + NOTE_SECONDS + 0.05);
            }
            return true;
        } catch {
            return false;
        }
    };
}

export const playWaitingChime = createWaitingChime();

/**
 * Toca o alerta quando chega alguém novo à sala de espera.
 * @param {Array<{ participantId: string }>} guests a sala de espera agora
 * @param {() => unknown} [play] o alerta (o som de verdade por padrão)
 */
export function useWaitingChime(guests, play = playWaitingChime) {
    // null até a primeira leitura: a lista que já existe ao montar conta como vista (o banner já está na tela)
    const seen = useRef(null);
    useEffect(() => {
        const current = (guests ?? []).map((guest) => guest.participantId);
        if (seen.current === null) {
            seen.current = new Set(current);
            return;
        }
        const arrivals = current.filter((id) => !seen.current.has(id));
        current.forEach((id) => seen.current.add(id));
        if (arrivals.length > 0) play();
    }, [guests, play]);
}
